/*
 * VENDORED from the nn-favorites app (extensions/list-blocks/assets/state-manager.js).
 * Wrapped in a guarded IIFE so it can coexist with the app's own blocks/embeds if
 * they are enabled later. Do not hand-edit — re-sync from the app when it updates.
 * Loaded by sections/product-hotspots.liquid for the shoppable-hotspot favorites toggle.
 */
(function () {
  if (window.NnFavoritesState) return;
// @ts-check

/**
 * @typedef {Object} ProductEntry
 * @property {string} id
 * @property {boolean} status
 */

/**
 * @typedef {Object} LogEntry
 * @property {string} date
 * @property {ProductEntry[]} products
 */

/**
 * @typedef {Object} Collection
 * @property {number} [id]
 * @property {string} [handle]
 * @property {string} [updated_at]
 */

/**
 * @typedef {Object} ListState
 * @property {Collection} [collection]
 * @property {LogEntry[]} log
 */

/**
 * Global state manager using a log-based reconciliation system.
 * Handles Shopify's caching delays by tracking state changes with timestamps.
 */
class NnFavoritesState extends EventTarget {
  static STORAGE_KEY = "nn-favorites-lists";

  /** @returns {Record<string, ListState>} */
  #load() {
    try {
      return JSON.parse(
        localStorage.getItem(NnFavoritesState.STORAGE_KEY) || "{}",
      );
    } catch {
      return {};
    }
  }

  /** @param {Record<string, ListState>} data */
  #save(data) {
    try {
      localStorage.setItem(NnFavoritesState.STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.warn("[NnFavorites] Failed to save to localStorage:", e);
    }
  }

  /**
   * @param {string} listType
   * @returns {ListState|null}
   */
  #get(listType) {
    return this.#load()[listType] || null;
  }

  /**
   * @param {string} listType
   * @param {ListState} entry
   */
  #set(listType, entry) {
    const all = this.#load();
    all[listType] = entry;
    this.#save(all);
    this.dispatchEvent(new CustomEvent("change", { detail: { listType } }));
  }

  /** @param {string} listType */
  #clear(listType) {
    const all = this.#load();
    if (!all[listType]) return;
    delete all[listType];
    this.#save(all);
    this.dispatchEvent(new CustomEvent("change", { detail: { listType } }));
  }

  /**
   * Set the full product list from server data.
   * If no collection, clears state (collection was deleted).
   * If incoming date <= current base date, discards incoming (stale).
   * If incoming date > current base date, replaces base and keeps newer mutations.
   * @param {string} listType
   * @param {Collection} [collection]
   * @param {string[]} [productIds]
   */
  setProducts(listType, collection, productIds = []) {
    if (!collection?.updated_at) {
      this.#clear(listType);
      return;
    }

    const existing = this.#get(listType);

    /** @type {LogEntry} */
    const baseEntry = {
      date: collection.updated_at,
      products: productIds.map((id) => ({ id, status: true })),
    };

    if (!existing?.log?.length) {
      this.#set(listType, { collection, log: [baseEntry] });
      return;
    }

    const currentBaseDate = new Date(existing.log[0]?.date || 0).getTime();
    const incomingDateMs = new Date(collection.updated_at).getTime();
    if (incomingDateMs <= currentBaseDate) {
      existing.collection = { ...existing.collection, ...collection };
      this.#set(listType, existing);
      return;
    }

    const entriesAfter = existing.log
      .slice(1)
      .filter((entry) => new Date(entry.date).getTime() > incomingDateMs);

    this.#set(listType, {
      collection: { ...existing.collection, ...collection },
      log: [baseEntry, ...entriesAfter],
    });
  }

  /**
   * @param {string} listType
   * @param {string} productId
   * @param {boolean} status
   * @param {Collection} [collection]
   */
  toggle(listType, productId, status, collection) {
    const existing = this.#get(listType) || { log: [] };

    if (collection)
      existing.collection = { ...existing.collection, ...collection };

    existing.log.push({
      date: new Date().toISOString(),
      products: [{ id: productId, status }],
    });

    this.#set(listType, existing);
  }

  /**
   * @param {string} listType
   * @returns {Set<string>}
   */
  getProducts(listType) {
    const state = this.#get(listType);
    if (!state?.log?.length) return new Set();

    /** @type {Map<string, boolean>} */
    const products = new Map();
    for (const entry of state.log) {
      for (const { id, status } of entry.products) {
        if (status) products.set(String(id), true);
        else products.delete(String(id));
      }
    }
    return new Set(products.keys());
  }

  /**
   * @param {string} listType
   * @returns {string|undefined}
   */
  getCollectionHandle(listType) {
    return this.#get(listType)?.collection?.handle;
  }

  /**
   * Whether the manager has any stored knowledge of this list type yet
   * (seeded by a header icon or mutated by a prior toggle). When false,
   * consumers should fall back to their server-rendered initial state.
   * @param {string} listType
   * @returns {boolean}
   */
  hasList(listType) {
    return Boolean(this.#get(listType)?.log?.length);
  }

  /**
   * @param {string} listType
   * @param {Collection} collection
   */
  updateCollection(listType, collection) {
    const existing = this.#get(listType);
    if (!existing) return;
    existing.collection = { ...existing.collection, ...collection };
    this.#set(listType, existing);
  }
}

/** @type {NnFavoritesState} */
// @ts-ignore - extending window
window.NnFavoritesState = new NnFavoritesState();
document.dispatchEvent(new CustomEvent("nn-favorites:ready"));

})();
